import { useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import AppHeader from '../../components/shell/AppHeader'
import { bundleByPerson } from './inboxOrder'
import SubmittedSchedule from './SubmittedSchedule'
import SupervisorNav from '../../components/supervisor/SupervisorNav'
import { useProfileIdentity } from '../../lib/useProfileIdentity'
import EmptyState from '../../components/shell/EmptyState'
import StatusChip, { type RecordState } from '../../components/agent/StatusChip'
import TeamSwitcher from '../../components/supervisor/TeamSwitcher'
import { supabase } from '../../lib/supabase'
import { useRefetchOnFocus } from '../../lib/useRefetchOnFocus'
import { formatTimesInText, useTimeFormat } from '../../lib/timeFormat'
import { schedulingServiceEnabled } from '../../lib/features'
import { getTeamLeaveRequests, type TeamLeaveRequest } from './leaveRequestQueue'
import { decideLeaveRequest, reverseLeaveDecision } from './leaveDecisionStore'
import { todayIso } from '../reports/reportDates'
import { getTeamAvailabilityExceptions, type TeamAvailabilityException } from './availabilityExceptionQueue'
import { decideAvailabilityException } from './availabilityDecisionStore'
import { getTeamOpenShiftClaims, type TeamOpenShiftClaim } from './openShiftClaimQueue'
import { decideOpenShiftClaim } from './openShiftDecisionStore'
import { getTeamShiftSwaps, type TeamShiftSwap } from './shiftSwapQueue'
import { decideShiftSwap } from './shiftSwapDecisionStore'
import type { SwapState } from '../agent/agentSwapStore'
import type { LeaveRequestState } from '../agent/leaveRequestStore'
import {
  getTeamScheduleSubmissions,
  type ScheduleSubmissionState,
  type TeamScheduleSubmission,
} from './scheduleSubmissionQueue'
import { decideScheduleSubmission, reverseScheduleDecision } from './scheduleDecisionStore'
import { clearOverridesForApprovedLeave } from './planningBoardDraftStore'
import { getBoardRoster, type BoardRosterRow } from './boardRosterStore'
import { getStaffingTargets, getTargetCatalog, type StaffingTarget } from './staffingTargetStore'
import { coverageFor, leaveImpact, WHOLE_SHIFT, type CoverageFinding, type CoveragePerson, type LeaveImpact } from './coverageAnalysis'
import { PAY_PERIOD_DATES } from '../agent/payPeriod19'
import { PAY_PERIOD_20_DATES } from '../agent/payPeriod20'
import { useSupervisorTeams } from './useSupervisorTeams'

/**
 * docs/design/journey-map.md's "Approval inbox": schedule submissions and
 * leave requests in one prioritized queue, soonest affected date first.
 * The team toggle (TeamSwitcher.tsx) scopes the queue to one of the
 * teams the signed-in supervisor owns, by the real team each request's
 * employee is placed on. A pending full-day leave request shows its effect
 * on coverage against the team's staffing targets (coverageAnalysis.ts):
 * supported, review recommended, or a conflict naming the assignment and days
 * it would leave short; approving one that would leave a day understaffed asks
 * to confirm first. Pattern signals and the request's proposed change are not
 * shown, and a target that asks for a qualification is not read here.
 *
 * Approve with supervisor modification (docs/design/journey-map.md's own
 * fourth decision) is a leave request's own: the supervisor picks which
 * of the request's own dates to actually approve, recorded on the
 * decision as modifiedDates (leaveDecisionStore.ts) rather than replacing
 * the request's own dates, and SupervisorPlanningBoardPage.tsx's own
 * approvedLeaveByCell reads that subset once one exists. A schedule
 * submission has no equivalent yet, since the approval queue carries no
 * day-by-day content of its own to modify, only a period and a date
 * range.
 */

const COMMENT_MAX_LENGTH = 500

type DecisionState = 'approved' | 'returned' | 'denied'

type InboxItem = {
  id: string
  kind: 'leave' | 'schedule-submission' | 'availability' | 'open-shift' | 'swap'
  employee: string
  employeeId: string
  requestType: string
  detail: string
  sortDate: string
  // The employee's real team (getEmployeeTeamMap), what the queue filters on.
  teamId: string
  comment?: string
  // Leave items only: the request's own full date range (TeamLeaveRequest's
  // own requestedDates, never narrowed), what Approve with supervisor
  // modification picks a subset of and what a decided item's own "Approved
  // X of Y requested days" compares against.
  dates?: string[]
  // Carried directly on TeamLeaveRequest/TeamScheduleSubmission, since the
  // real tables and decisions are already joined by the time
  // leaveRequestQueue.ts and scheduleSubmissionQueue.ts read them.
  state?: LeaveRequestState | ScheduleSubmissionState | SwapState
  decisionComment?: string
  decidedByName?: string
  // Leave items only: the narrowed subset an approve_modified decision
  // actually kept, present only once one exists.
  modifiedDates?: string[]
  // Leave items only: whether the request itself is a full day, what
  // gates clearOverridesForApprovedLeave below (a partial-day approval
  // never overrides a board cell in the first place, so nothing on it
  // needs clearing).
  fullDay?: boolean
  // Leave items and schedule submissions: the last day of the leave or of the
  // pay period, after which a decision on it can no longer be reversed.
  endDate?: string
  // Schedule submissions only: the first day of the pay period.
  periodStart?: string
  // Open shift claims only: what to weigh before approving.
  advisories?: string[]
}

// Two sources merged into one queue: real leave requests and real
// schedule submissions ('schedule-submission', since
// 20260922000000_schedule_submissions.sql). Every item carries the
// record's own uuid, and sortDate gives the inbox its "soonest affected date
// first" order within each section, with each person's requests kept together (inboxOrder.ts).
function buildInboxQueue(
  leaveRequests: TeamLeaveRequest[],
  scheduleSubmissions: TeamScheduleSubmission[],
  availabilityExceptions: TeamAvailabilityException[],
  openShiftClaims: TeamOpenShiftClaim[],
  shiftSwaps: TeamShiftSwap[],
): InboxItem[] {
  return [
    ...leaveRequests.map(
      (item): InboxItem => ({
        id: item.id,
        kind: 'leave',
        employee: item.employeeName,
        employeeId: item.employeeId,
        requestType: 'Leave request',
        detail: `${item.leaveType}, ${item.dateRange}`,
        sortDate: item.sortDate,
        teamId: item.teamId,
        comment: item.comment,
        dates: item.requestedDates,
        state: item.state,
        decisionComment: item.decisionComment,
        decidedByName: item.decidedByName,
        // TeamLeaveRequest.dates is the request's own full range until a
        // decision narrows it (leaveRequestQueue.ts); a length mismatch
        // against requestedDates is what approve_modified actually looks
        // like once decided, with no separate modifiedDates field of its
        // own to read instead.
        modifiedDates: item.dates.length !== item.requestedDates.length ? item.dates : undefined,
        fullDay: item.fullDay,
        endDate: item.endDate,
      }),
    ),
    ...scheduleSubmissions.map(
      (item): InboxItem => ({
        id: item.id,
        kind: 'schedule-submission',
        employee: item.employeeName,
        employeeId: item.employeeId,
        requestType: 'Schedule submission',
        detail: `${item.periodLabel}, ${item.dateRange}`,
        sortDate: item.sortDate,
        teamId: item.teamId,
        endDate: item.periodEnd,
        periodStart: item.periodStart,
        state: item.state,
        decisionComment: item.decisionComment,
        decidedByName: item.decidedByName,
      }),
    ),
    ...availabilityExceptions.map(
      (item): InboxItem => ({
        id: item.id,
        kind: 'availability',
        employee: item.employeeName,
        employeeId: item.employeeId,
        requestType: 'Availability exception',
        detail: item.detail,
        sortDate: item.sortDate,
        teamId: item.teamId,
        comment: item.reason,
        state: item.state,
        decisionComment: item.decisionComment,
        decidedByName: item.decidedByName,
      }),
    ),
    ...openShiftClaims.map(
      (item): InboxItem => ({
        id: item.id,
        kind: 'open-shift',
        employee: item.employeeName,
        employeeId: item.employeeId,
        requestType: 'Open shift claim',
        detail: item.detail,
        sortDate: item.date,
        teamId: item.teamId,
        comment: item.comment,
        state: item.state,
        decisionComment: item.decisionComment,
        decidedByName: item.decidedByName,
        advisories: item.advisories,
      }),
    ),
    ...shiftSwaps.map(
      (item): InboxItem => ({
        id: item.id,
        kind: 'swap',
        employee: `${item.requesterName} and ${item.counterpartName}`,
        employeeId: item.requesterId,
        requestType: 'Shift swap',
        detail: item.detail,
        sortDate: item.sortDate,
        teamId: item.teamId,
        comment: item.message,
        state: item.state,
        decisionComment: item.decisionComment,
        decidedByName: item.decidedByName,
        advisories: item.advisories,
      }),
    ),
  ]
}

function CalendarPlusIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="3" y="4" width="18" height="18" rx="2" />
      <path d="M16 2v4M8 2v4M3 10h18M12 14v4M10 16h4" />
    </svg>
  )
}

function SwapIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M7 7h13l-3-3M17 17H4l3 3" />
    </svg>
  )
}

function SubmissionIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M22 2 11 13M22 2 15 22l-4-9-9-4Z" />
    </svg>
  )
}

const KIND_META = {
  leave: { icon: <CalendarPlusIcon />, iconClass: 'bg-info/10 text-info' },
  schedule: { icon: <SubmissionIcon />, iconClass: 'bg-info/10 text-info' },
  'schedule-submission': { icon: <SubmissionIcon />, iconClass: 'bg-info/10 text-info' },
  availability: { icon: <CalendarPlusIcon />, iconClass: 'bg-info/10 text-info' },
  'open-shift': { icon: <CalendarPlusIcon />, iconClass: 'bg-info/10 text-info' },
  swap: { icon: <SwapIcon />, iconClass: 'bg-info/10 text-info' },
} as const

// "Sep 22" from an ISO date, for the modification panel's own per-date
// checkboxes, the same short shape the Coverage gaps panel's own date
// title uses elsewhere in the supervisor workspace.
function formatRequestDate(dateIso: string): string {
  return new Date(`${dateIso}T00:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

type OpenComment = { requestId: string; kind: 'returned' | 'denied' | 'modified' | 'reversed' }

// One line per day the leave would leave short: "Case Processing would be
// understaffed on Sep 26 (2 of 3 needed)".
function impactLines(impact: LeaveImpact): string[] {
  return impact.days.flatMap((day) =>
    day.findings.map(
      (finding) =>
        `${finding.assignment} would be ${finding.state === 'understaffed' ? 'understaffed' : 'at risk'} on ${formatRequestDate(day.date)} (${finding.actual} of ${finding.required} needed)`,
    ),
  )
}

export default function SupervisorApprovalInboxPage() {
  const identity = useProfileIdentity()
  // The times in a request's description, in the account's own format.
  const { format } = useTimeFormat()
  const [userId, setUserId] = useState<string | null>(null)
  const [sessionReady, setSessionReady] = useState(false)
  const { teams, currentTeamId, teamsLoaded, switchTeam } = useSupervisorTeams(userId)
  const [leaveRequests, setLeaveRequests] = useState<TeamLeaveRequest[]>([])
  const [scheduleSubmissions, setScheduleSubmissions] = useState<TeamScheduleSubmission[]>([])
  const [availabilityExceptions, setAvailabilityExceptions] = useState<TeamAvailabilityException[]>([])
  const [openShiftClaims, setOpenShiftClaims] = useState<TeamOpenShiftClaim[]>([])
  const [shiftSwaps, setShiftSwaps] = useState<TeamShiftSwap[]>([])
  // Why a claim could not be decided, when the function says.
  const [decisionMessage, setDecisionMessage] = useState<string | null>(null)
  const [openComment, setOpenComment] = useState<OpenComment | null>(null)
  const [commentDraft, setCommentDraft] = useState('')
  // Which of a leave request's own dates the supervisor currently has
  // checked in the modification panel; seeded to every one of them when
  // the panel opens, per docs/design/journey-map.md's "Approve with
  // supervisor modification."
  const [modifiedDatesDraft, setModifiedDatesDraft] = useState<Set<string>>(new Set())
  const [decisionFailed, setDecisionFailed] = useState(false)
  // What coverage is measured from: the team's schedules for both pay periods,
  // its staffing targets, and its shift, so each pending leave request can be
  // read against them before it is decided.
  const [roster, setRoster] = useState<{ current: BoardRosterRow[]; next: BoardRosterRow[] }>({ current: [], next: [] })
  const [targets, setTargets] = useState<StaffingTarget[]>([])
  const [shiftName, setShiftName] = useState('')
  // The request whose Approve is waiting on a confirmation because it would
  // leave a day understaffed.
  const [approveConfirmFor, setApproveConfirmFor] = useState<string | null>(null)
  const approveTriggerRefs = useRef<Map<string, HTMLButtonElement>>(new Map())
  // Null until the profile fetch actually succeeds; decide() below
  // refuses to record a decision while it is null, rather than falling
  // back to a name that is not the signed-in supervisor's own.
  const [supervisorName, setSupervisorName] = useState<string | null>(null)
  // A decided or reopened item moves to the other section's list, unmounting
  // the button the keyboard user just activated along with the card it was
  // on; moving focus to the section it landed in, rather than leaving the
  // browser to drop it on <body>, keeps a keyboard user oriented.
  const pendingHeadingRef = useRef<HTMLHeadingElement>(null)
  const decidedHeadingRef = useRef<HTMLHeadingElement>(null)
  // Cancelling a comment panel unmounts it, and the Cancel button along
  // with it, the same class of problem; keyed by requestId and kind since
  // Return and Deny each open their own panel on the same card. Restores
  // focus to whichever of the two triggered the panel being closed.
  const commentTriggerRefs = useRef<Map<string, HTMLButtonElement>>(new Map())

  // Deciding needs userId, so the queue waits for the session to resolve
  // before it renders interactive: rendering it immediately would let a
  // decision fire while userId is still null, the same race
  // AgentRequestLeavePage.tsx's own sessionReady guards against. Waiting
  // on the profile fetch too, rather than resolving sessionReady as soon
  // as the session itself does, closes the same race for supervisorName.
  // Reloads the real leave requests and schedule submissions a decision
  // could land on from elsewhere: another supervisor tab, another device,
  // or the agent's own resubmission. Nothing here subscribes to realtime
  // changes, so a tab left open on this page would otherwise keep showing
  // exactly what it fetched at mount, the same gap AgentDashboardPage.tsx's
  // own loadRealData closes for an agent's requests.
  async function loadQueues() {
    setLeaveRequests(await getTeamLeaveRequests())
    setScheduleSubmissions(await getTeamScheduleSubmissions())
    // Availability exceptions, open shift claims, and swaps belong to the
    // scheduling service; without it the queue holds leave and submissions only.
    if (!schedulingServiceEnabled()) return
    setAvailabilityExceptions(await getTeamAvailabilityExceptions())
    setOpenShiftClaims(await getTeamOpenShiftClaims())
    setShiftSwaps(await getTeamShiftSwaps())
  }

  useEffect(() => {
    let cancelled = false
    supabase.auth
      .getSession()
      .then(async ({ data: { session } }) => {
        if (cancelled) return
        const id = session?.user.id
        if (id) {
          setUserId(id)
          await loadQueues()
          const { data: profile } = await supabase
            .from('profiles')
            .select('first_name, last_name')
            .eq('id', id)
            .single()
          if (!cancelled && profile) {
            setSupervisorName(`${profile.first_name} ${profile.last_name}`)
          }
        }
      })
      .finally(() => {
        if (!cancelled) setSessionReady(true)
      })
    return () => {
      cancelled = true
    }
  }, [])

  useRefetchOnFocus(() => {
    if (userId) loadQueues()
  })

  // A link from the dashboard or a notification names one request. Once the
  // queue holds it, the page moves to its team if needed, scrolls to its card,
  // and puts focus there, once per link; the card is marked while the link names it.
  const [searchParams] = useSearchParams()
  const requestedId = searchParams.get('request')
  const handledRequest = useRef<string | null>(null)
  // The card the supervisor last clicked or moved into; until they pick one,
  // the request a link named.
  // Clicking empty space clears it (false).
  const [pickedId, setPickedId] = useState<string | false | null>(null)
  const markedId = pickedId === null ? requestedId : pickedId || null
  useEffect(() => {
    if (!requestedId || handledRequest.current === requestedId) return
    const target = buildInboxQueue(leaveRequests, scheduleSubmissions, availabilityExceptions, openShiftClaims, shiftSwaps).find((item) => item.id === requestedId)
    if (!target || !currentTeamId) return
    if (target.teamId !== currentTeamId) {
      switchTeam(target.teamId)
      return
    }
    const card = document.getElementById(`request-${requestedId}`)
    if (!card) return
    handledRequest.current = requestedId
    card.scrollIntoView?.({ block: 'center' })
    card.focus({ preventScroll: true })
  }, [requestedId, currentTeamId, switchTeam, leaveRequests, scheduleSubmissions, availabilityExceptions, openShiftClaims, shiftSwaps])

  useEffect(() => {
    if (!currentTeamId) return
    let cancelled = false
    Promise.all([getBoardRoster(currentTeamId, PAY_PERIOD_DATES, PAY_PERIOD_20_DATES), getStaffingTargets(), getTargetCatalog(currentTeamId)]).then(
      ([loadedRoster, loadedTargets, catalog]) => {
        if (cancelled) return
        setRoster(loadedRoster)
        setTargets(loadedTargets)
        setShiftName(catalog.shift?.name ?? '')
      },
    )
    return () => {
      cancelled = true
    }
  }, [currentTeamId])

  function decisionFor(
    item: InboxItem,
  ): { state: RecordState; comment?: string; supervisor: string; modifiedDates?: string[] } | undefined {
    // Already carried directly on the item (buildInboxQueue): the real
    // tables and decisions are already joined by the time
    // leaveRequestQueue.ts and scheduleSubmissionQueue.ts read them. Not
    // yet decided is 'pending' for a leave request, 'submitted' for a
    // schedule submission (their own state enums each start there); a
    // decided state (never either of those two) is always a valid
    // RecordState on its own.
    // item.state is typed as the full LeaveRequestState | ScheduleSubmissionState
    // union regardless of item.kind (InboxItem is not a discriminated
    // union on the two), so the cast below only restates what the two
    // runtime checks above it already guarantee: neither 'pending' nor
    // 'submitted' survives past them.
    if (item.kind === 'leave' && item.state && item.state !== 'pending') {
      return {
        state: item.state as RecordState,
        comment: item.decisionComment,
        supervisor: item.decidedByName ?? '',
        modifiedDates: item.modifiedDates,
      }
    }
    if (item.kind === 'availability' && item.state && item.state !== 'pending') {
      return {
        state: item.state as RecordState,
        comment: item.decisionComment,
        supervisor: item.decidedByName ?? '',
      }
    }
    // A swap awaits the supervisor once both agents have agreed ('accepted').
    if (item.kind === 'swap' && item.state && item.state !== 'accepted') {
      return {
        state: item.state as RecordState,
        comment: item.decisionComment,
        supervisor: item.decidedByName ?? '',
      }
    }
    if (item.kind === 'open-shift' && item.state && item.state !== 'pending') {
      return {
        state: item.state as RecordState,
        comment: item.decisionComment,
        supervisor: item.decidedByName ?? '',
      }
    }
    if (item.kind === 'schedule-submission' && item.state && item.state !== 'submitted') {
      return {
        state: item.state as RecordState,
        comment: item.decisionComment,
        supervisor: item.decidedByName ?? '',
        modifiedDates: item.modifiedDates,
      }
    }
    return undefined
  }

  function openCommentFor(requestId: string, kind: 'returned' | 'denied' | 'reversed') {
    setApproveConfirmFor(null)
    const alreadyOpen = openComment?.requestId === requestId && openComment.kind === kind
    setOpenComment(alreadyOpen ? null : { requestId, kind })
    setCommentDraft('')
  }

  function openModificationFor(item: InboxItem) {
    setApproveConfirmFor(null)
    const alreadyOpen = openComment?.requestId === item.id && openComment.kind === 'modified'
    setOpenComment(alreadyOpen ? null : { requestId: item.id, kind: 'modified' })
    setCommentDraft('')
    setModifiedDatesDraft(new Set(item.dates ?? []))
  }

  function toggleModifiedDate(date: string) {
    setModifiedDatesDraft((current) => {
      const next = new Set(current)
      if (next.has(date)) next.delete(date)
      else next.add(date)
      return next
    })
  }

  // Takes back an approval or a denial of a leave request, or the approval of a
  // schedule submission: it is awaiting a decision again and the agent is told.
  // Only until the leave or the pay period has ended.
  async function reverse(item: InboxItem, reason: string) {
    const reversed =
      item.kind === 'schedule-submission'
        ? await reverseScheduleDecision({ scheduleSubmissionId: item.id, reason })
        : await reverseLeaveDecision({ leaveRequestId: item.id, reason })
    if (!reversed.ok) {
      setDecisionMessage(reversed.message)
      return
    }
    setDecisionMessage(null)
    await loadQueues()
    setOpenComment(null)
    setCommentDraft('')
    pendingHeadingRef.current?.focus()
  }

  async function decide(item: InboxItem, state: DecisionState, comment?: string, modifiedDates?: string[]) {
    // supervisorName stays null when the profile fetch above failed or
    // returned no row; recording a decision under an unknown identity is
    // worse than refusing it, the same as userId being unavailable.
    if (!userId || !supervisorName) {
      setDecisionFailed(true)
      return
    }

    if (item.kind === 'leave') {
      const decided = await decideLeaveRequest({
        leaveRequestId: item.id,
        decision: state === 'approved' ? (modifiedDates ? 'approve_modified' : 'approve') : state === 'returned' ? 'return' : 'deny',
        comment,
        modifiedDates,
      })
      if (!decided) {
        setDecisionFailed(true)
        return
      }
      // v1-decide-leave-request already moved the row's own state, which the
      // queues are read again for below rather than patching one item in
      // place; clearOverridesForApprovedLeave needs a fresh read for the same
      // reason: the decision ran server-side, inside the same request, not
      // locally.

      // A board override saved on this cell before this approval was set
      // blind to the leave; entryFor's own precedence would otherwise let
      // it go on masking the leave it should now be showing instead. Only
      // relevant once the leave is actually approved, so 'returned' and
      // 'denied' skip this: neither ever reaches the board as appliedLeave.
      // A partial-day approval is also skipped: entryFor only lets a
      // full-day approved leave override a cell at all, so an override
      // already on a partial-day one is still exactly what the board
      // should keep showing, not something this approval just made stale.
      if (state === 'approved' && item.fullDay) {
        // The board's draft is keyed by the employee's real team id, the
        // same one the request itself already carries.
        const teamId = item.teamId
        if (teamId) {
          clearOverridesForApprovedLeave(userId, teamId, item.employeeId, modifiedDates ?? item.dates ?? [])
        }
      }
    } else if (item.kind === 'schedule-submission') {
      const decided = await decideScheduleSubmission({
        scheduleSubmissionId: item.id,
        decision: state === 'approved' ? 'approve' : state === 'returned' ? 'return' : 'deny',
        comment,
      })
      if (!decided) {
        setDecisionFailed(true)
        return
      }
      // v1-decide-schedule-submission already moved the row's own state. No
      // planning-board side effect: an approved schedule submission's own
      // day-by-day content is not applied to the board yet (a separate,
      // larger slice).
    } else if (item.kind === 'availability') {
      const decided = await decideAvailabilityException({
        exceptionId: item.id,
        decision: state === 'approved' ? 'approve' : state === 'returned' ? 'return' : 'deny',
        comment,
      })
      if (!decided) {
        setDecisionFailed(true)
        return
      }
    } else if (item.kind === 'open-shift') {
      const decided = await decideOpenShiftClaim({
        claimId: item.id,
        decision: state === 'approved' ? 'approve' : 'deny',
        comment,
      })
      if (!decided.ok) {
        setDecisionMessage(decided.message)
        return
      }
      setDecisionMessage(null)
      // An approval also filled the slot and denied the other claims for it.
    } else if (item.kind === 'swap') {
      const decided = await decideShiftSwap({
        swapId: item.id,
        decision: state === 'approved' ? 'approve' : 'deny',
        comment,
      })
      if (!decided.ok) {
        setDecisionMessage(decided.message)
        return
      }
      setDecisionMessage(null)
    }

    // A decision can change what another kind of request is read against: an
    // approved claim or swap changes the hours an agent is scheduled, which a
    // pending swap's tour of duty warning reads, and an approved availability
    // exception changes the hours an agent is marked unavailable for, which
    // every claim and swap warning reads. So every queue is read again, not only
    // the one the decision was in.
    await loadQueues()

    setOpenComment(null)
    setApproveConfirmFor(null)
    setCommentDraft('')
    setDecisionFailed(false)
    decidedHeadingRef.current?.focus()
  }

  const teamName = teams.find((team) => team.id === currentTeamId)?.name ?? ''

  // Everyone on the team's schedule that date as it stands: their entry, with any
  // full-day leave already approved counted as leave.
  function peopleOn(date: string): CoveragePerson[] {
    const currentIndex = PAY_PERIOD_DATES.findIndex((day) => day.date === date)
    const nextIndex = PAY_PERIOD_20_DATES.findIndex((day) => day.date === date)
    const rows = currentIndex >= 0 ? roster.current : nextIndex >= 0 ? roster.next : []
    const index = currentIndex >= 0 ? currentIndex : nextIndex
    return rows.flatMap((row) => {
      const day = row.days[index]
      if (!day || !row.teamDates.has(date)) return []
      const onLeave = leaveRequests.some((request) => request.state === 'approved' && request.fullDay && request.employeeId === row.id && request.dates.includes(date))
      return [onLeave ? { id: row.id, category: 'leave' as const } : { id: row.id, category: day.category, detail: day.detail, countsAsAvailable: day.countsAsAvailable }]
    })
  }

  // What approving this full-day leave for these dates does to coverage.
  function impactFor(item: InboxItem, dates: string[]): LeaveImpact | null {
    if (item.kind !== 'leave' || !item.fullDay) return null
    return leaveImpact({ employeeId: item.employeeId, dates, peopleOn, targets, team: teamName, shift: shiftName })
  }
  // Where a pay period submission, kept as it stands, leaves the figures this
  // officer's own day counts toward over or under the targets: their
  // assignment, and the whole shift.
  function conflictsOn(employeeId: string, date: string): CoverageFinding[] {
    const people = peopleOn(date)
    const person = people.find((candidate) => candidate.id === employeeId)
    if (!person || person.category !== 'work') return []
    return coverageFor(people, targets, { date, team: teamName, shift: shiftName }).filter(
      (finding) => (finding.assignment === person.detail || finding.assignment === WHOLE_SHIFT) && (finding.state === 'overstaffed' || finding.state === 'understaffed'),
    )
  }
  const teamHasTargets = targets.some((target) => target.team === teamName)
  const teamQueue = buildInboxQueue(leaveRequests, scheduleSubmissions, availabilityExceptions, openShiftClaims, shiftSwaps).filter((item) => item.teamId === currentTeamId)
  // Each section is ordered on its own, so a request in the other section
  // never decides where a person's requests sit in this one.
  const pending = bundleByPerson(teamQueue.filter((item) => !decisionFor(item)))
  const decided = bundleByPerson(teamQueue.filter((item) => decisionFor(item)))

  if (!sessionReady || (userId && !teamsLoaded)) {
    return (
      <div className="flex h-dvh flex-col bg-surface text-ink">
        <div className="flex flex-1 flex-col items-center justify-center gap-4 p-4">
          <p role="status" className="text-small text-ink-muted">
            Loading&hellip;
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="flex h-dvh flex-col bg-surface text-ink">
      <a
        href="#approval-inbox-main"
        className="sr-only focus:not-sr-only focus:absolute focus:z-50 focus:m-2 focus:rounded focus:bg-canvas focus:px-4 focus:py-2 focus:font-semibold"
      >
        Skip to main content
      </a>

      <AppHeader initials={identity?.initials ?? ''} />

      <main
        id="approval-inbox-main"
        tabIndex={0}
        onClick={(event) => {
          if (!(event.target as HTMLElement).closest('[data-request-card]')) setPickedId(false)
        }}
        className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8"
      >
        <SupervisorNav />
        <div className="mx-auto flex w-full max-w-[1100px] flex-col gap-5">
          <div>
            <h1 className="text-h2 font-extrabold">Approval Inbox</h1>
            <p className="mt-1 text-small text-ink-muted">
              {teamName} &middot; Leave requests, schedule submissions, availability exceptions, open shift claims, and shift swaps, soonest affected date first. Approve,
              approve with modification, return for revision, or deny each one.
            </p>
          </div>

          <TeamSwitcher teams={teams} currentTeamId={currentTeamId} onChange={switchTeam} />

          {decisionFailed && (
            <p role="alert" className="text-caption font-semibold text-error">
              Could not save the change. Storage may be unavailable in this browser, or your own account details
              could not be loaded; reload the page or try a different browser.
            </p>
          )}

          {decisionMessage && (
            <p role="alert" className="text-caption font-semibold text-error">
              {decisionMessage}
            </p>
          )}

          <section aria-labelledby="inbox-pending">
            <h2
              id="inbox-pending"
              ref={pendingHeadingRef}
              tabIndex={-1}
              className="text-caption font-bold tracking-wide text-ink-subtle uppercase"
            >
              Awaiting a decision
            </h2>
            {pending.length === 0 ? (
              <EmptyState>No requests are waiting on a decision.</EmptyState>
            ) : (
              <ul className="mt-2 flex flex-col gap-3">
                {pending.map((item) => {
                  const meta = KIND_META[item.kind]
                  const returnOpen = openComment?.requestId === item.id && openComment.kind === 'returned'
                  const denyOpen = openComment?.requestId === item.id && openComment.kind === 'denied'
                  const modifyOpen = openComment?.requestId === item.id && openComment.kind === 'modified'
                  // What approving the whole request does to coverage.
                  const impact = impactFor(item, item.dates ?? [])
                  const modifyImpact = modifyOpen ? impactFor(item, [...modifiedDatesDraft]) : null

                  return (
                    <li
                      key={item.id}
                      id={`request-${item.id}`}
                      data-request-card
                      tabIndex={-1}
                      onClick={() => setPickedId(item.id)}
                      onFocus={() => setPickedId(item.id)}
                      className={`rounded-surface border bg-canvas p-4 outline-none ${markedId === item.id ? 'border-primary ring-2 ring-primary' : 'border-line'}`}
                    >
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-3">
                          <span
                            className={`flex size-8 flex-none items-center justify-center rounded-full ${meta.iconClass}`}
                          >
                            {meta.icon}
                          </span>
                          <div>
                            <p className="text-small font-bold">{item.employee}</p>
                            <p className="mt-0.5 text-small text-ink-muted">
                              {item.requestType} &middot; {formatTimesInText(item.detail, format)}
                            </p>
                          </div>
                        </div>
                        <StatusChip state="submitted" />
                      </div>

                      {item.comment && (
                        <p className="mt-2 text-small text-ink-muted">&ldquo;{item.comment}&rdquo;</p>
                      )}

                      {item.kind === 'schedule-submission' && item.periodStart && item.endDate && (
                        <SubmittedSchedule employeeId={item.employeeId} employee={item.employee} periodStart={item.periodStart} periodEnd={item.endDate} conflictsOn={(date) => conflictsOn(item.employeeId, date)} />
                      )}

                      {item.advisories && item.advisories.length > 0 && (
                        <ul className="mt-2 list-disc pl-5 text-small font-semibold text-warning">
                          {item.advisories.map((advisory) => (
                            <li key={advisory}>{formatTimesInText(advisory, format)}</li>
                          ))}
                        </ul>
                      )}

                      {impact && (impact.level !== 'supported' || teamHasTargets) && (
                        <div className="mt-2 text-small">
                          {impact.level === 'supported' ? (
                            <p className="font-semibold text-success">Coverage supports approval.</p>
                          ) : (
                            <>
                              <p className={`font-bold ${impact.level === 'conflict' ? 'text-error' : 'text-warning'}`}>
                                {impact.level === 'conflict' ? 'Coverage conflict identified' : 'Review recommended'}
                              </p>
                              <ul className="list-disc pl-5 text-ink-muted">
                                {impactLines(impact).map((line) => (
                                  <li key={line}>{line}.</li>
                                ))}
                              </ul>
                            </>
                          )}
                        </div>
                      )}

                      <div className="mt-3 flex flex-wrap gap-2">
                        <button
                          type="button"
                          ref={(element) => {
                            if (element) approveTriggerRefs.current.set(item.id, element)
                            else approveTriggerRefs.current.delete(item.id)
                          }}
                          onClick={() => {
                            // Approval that would leave a day understaffed asks first.
                            if (impact?.level === 'conflict') {
                              setOpenComment(null)
                              setApproveConfirmFor(item.id)
                            } else {
                              decide(item, 'approved')
                            }
                          }}
                          className="flex min-h-11 items-center justify-center rounded-control bg-primary px-4 text-small font-bold text-primary-ink hover:bg-primary-hover"
                        >
                          Approve
                        </button>
                        {item.kind === 'leave' && (
                          <button
                            type="button"
                            ref={(element) => {
                              if (element) commentTriggerRefs.current.set(`${item.id}-modified`, element)
                              else commentTriggerRefs.current.delete(`${item.id}-modified`)
                            }}
                            aria-expanded={modifyOpen}
                            onClick={() => openModificationFor(item)}
                            className="flex min-h-11 items-center justify-center rounded-control border border-line-strong px-4 text-small font-bold hover:bg-surface-2"
                          >
                            Approve with modification
                          </button>
                        )}
                        {item.kind !== 'open-shift' && item.kind !== 'swap' && (
                          <button
                            type="button"
                            ref={(element) => {
                              if (element) commentTriggerRefs.current.set(`${item.id}-returned`, element)
                              else commentTriggerRefs.current.delete(`${item.id}-returned`)
                            }}
                            onClick={() => openCommentFor(item.id, 'returned')}
                            className="flex min-h-11 items-center justify-center rounded-control border border-line-strong px-4 text-small font-bold hover:bg-surface-2"
                          >
                            Return for revision
                          </button>
                        )}
                        <button
                          type="button"
                          ref={(element) => {
                            if (element) commentTriggerRefs.current.set(`${item.id}-denied`, element)
                            else commentTriggerRefs.current.delete(`${item.id}-denied`)
                          }}
                          onClick={() => openCommentFor(item.id, 'denied')}
                          className="ml-auto flex min-h-11 items-center justify-center rounded-control border border-error px-4 text-small font-bold text-error hover:bg-error/10"
                        >
                          Deny
                        </button>
                      </div>

                      {approveConfirmFor === item.id && impact && (
                        <div role="group" aria-label={`Confirm approval for ${item.employee}`} className="mt-3 rounded-surface border border-error p-3">
                          <p role="alert" className="text-small font-bold text-error">
                            Approving this leave would leave {impactLines(impact).length === 1 ? 'a day' : 'days'} understaffed.
                          </p>
                          <ul className="mt-1 list-disc pl-5 text-small text-ink-muted">
                            {impactLines(impact).map((line) => (
                              <li key={line}>{line}.</li>
                            ))}
                          </ul>
                          <div className="mt-2 flex gap-2">
                            <button
                              type="button"
                              onClick={() => decide(item, 'approved')}
                              className="flex min-h-11 items-center justify-center rounded-control bg-primary px-4 text-small font-bold text-primary-ink hover:bg-primary-hover"
                            >
                              Approve anyway
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                approveTriggerRefs.current.get(item.id)?.focus()
                                setApproveConfirmFor(null)
                              }}
                              className="flex min-h-11 items-center justify-center rounded-control border border-line-strong px-4 text-small font-bold hover:bg-surface-2"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      )}

                      {(returnOpen || denyOpen) && (
                        <div className="mt-3 flex flex-col gap-2">
                          <label
                            htmlFor={`inbox-comment-${item.id}`}
                            className="text-caption font-bold text-ink-muted"
                          >
                            Comment (required)
                          </label>
                          <textarea
                            id={`inbox-comment-${item.id}`}
                            value={commentDraft}
                            onChange={(event) => setCommentDraft(event.target.value.slice(0, COMMENT_MAX_LENGTH))}
                            rows={2}
                            className="w-full rounded-control border border-line-strong p-2.5 text-small"
                          />
                          <div className="flex gap-2">
                            <button
                              type="button"
                              disabled={commentDraft.trim() === ''}
                              onClick={() => decide(item, returnOpen ? 'returned' : 'denied', commentDraft.trim())}
                              className={`flex min-h-11 items-center justify-center rounded-control px-4 text-small font-bold disabled:opacity-40 ${
                                returnOpen
                                  ? 'bg-primary text-primary-ink hover:bg-primary-hover'
                                  : 'border border-error text-error hover:bg-error/10'
                              }`}
                            >
                              {returnOpen ? 'Confirm return' : 'Confirm denial'}
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                commentTriggerRefs.current.get(`${item.id}-${returnOpen ? 'returned' : 'denied'}`)?.focus()
                                setOpenComment(null)
                                setCommentDraft('')
                              }}
                              className="flex min-h-11 items-center justify-center rounded-control border border-line-strong px-4 text-small font-bold hover:bg-surface-2"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      )}

                      {modifyOpen && item.dates && (
                        <div className="mt-3 flex flex-col gap-2">
                          <fieldset>
                            <legend className="text-caption font-bold text-ink-muted">Approved dates</legend>
                            <div className="mt-1 flex flex-wrap gap-3">
                              {item.dates.map((date) => (
                                <label key={date} className="flex items-center gap-1.5 text-small">
                                  <input
                                    type="checkbox"
                                    checked={modifiedDatesDraft.has(date)}
                                    onChange={() => toggleModifiedDate(date)}
                                    className="size-4"
                                  />
                                  {formatRequestDate(date)}
                                </label>
                              ))}
                            </div>
                          </fieldset>
                          {modifyImpact && modifyImpact.level !== 'supported' && (
                            <div className="text-small">
                              <p className={`font-bold ${modifyImpact.level === 'conflict' ? 'text-error' : 'text-warning'}`}>
                                {modifyImpact.level === 'conflict' ? 'Coverage conflict identified for these dates' : 'Review recommended for these dates'}
                              </p>
                              <ul className="list-disc pl-5 text-ink-muted">
                                {impactLines(modifyImpact).map((line) => (
                                  <li key={line}>{line}.</li>
                                ))}
                              </ul>
                            </div>
                          )}
                          <label
                            htmlFor={`inbox-modify-comment-${item.id}`}
                            className="text-caption font-bold text-ink-muted"
                          >
                            Comment (optional)
                          </label>
                          <textarea
                            id={`inbox-modify-comment-${item.id}`}
                            value={commentDraft}
                            onChange={(event) => setCommentDraft(event.target.value.slice(0, COMMENT_MAX_LENGTH))}
                            rows={2}
                            className="w-full rounded-control border border-line-strong p-2.5 text-small"
                          />
                          <div className="flex gap-2">
                            <button
                              type="button"
                              disabled={modifiedDatesDraft.size === 0}
                              onClick={() =>
                                decide(item, 'approved', commentDraft.trim() || undefined, [...modifiedDatesDraft])
                              }
                              className="flex min-h-11 items-center justify-center rounded-control bg-primary px-4 text-small font-bold text-primary-ink hover:bg-primary-hover disabled:opacity-40"
                            >
                              {modifyImpact?.level === 'conflict' ? 'Approve anyway' : 'Confirm modification'}
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                commentTriggerRefs.current.get(`${item.id}-modified`)?.focus()
                                setOpenComment(null)
                                setCommentDraft('')
                              }}
                              className="flex min-h-11 items-center justify-center rounded-control border border-line-strong px-4 text-small font-bold hover:bg-surface-2"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      )}
                    </li>
                  )
                })}
              </ul>
            )}
          </section>

          <section aria-labelledby="inbox-decided">
            <h2
              id="inbox-decided"
              ref={decidedHeadingRef}
              tabIndex={-1}
              className="text-caption font-bold tracking-wide text-ink-subtle uppercase"
            >
              Decided
            </h2>
            {decided.length === 0 ? (
              <EmptyState>No decisions yet this session.</EmptyState>
            ) : (
              <ul className="mt-2 flex flex-col gap-3">
                {decided.map((item) => {
                  const decision = decisionFor(item)
                  if (!decision) return null
                  const meta = KIND_META[item.kind]
                  return (
                    <li
                      key={item.id}
                      id={`request-${item.id}`}
                      data-request-card
                      tabIndex={-1}
                      onClick={() => setPickedId(item.id)}
                      onFocus={() => setPickedId(item.id)}
                      className={`rounded-surface border bg-canvas p-4 outline-none ${markedId === item.id ? 'border-primary ring-2 ring-primary' : 'border-line'}`}
                    >
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-3">
                          <span
                            className={`flex size-8 flex-none items-center justify-center rounded-full ${meta.iconClass}`}
                          >
                            {meta.icon}
                          </span>
                          <div>
                            <p className="text-small font-bold">{item.employee}</p>
                            <p className="mt-0.5 text-small text-ink-muted">
                              {item.requestType} &middot; {formatTimesInText(item.detail, format)}
                            </p>
                          </div>
                        </div>
                        <StatusChip state={decision.state} />
                      </div>
                      {decision.modifiedDates && item.dates && decision.modifiedDates.length !== item.dates.length && (
                        <p className="mt-2 text-small text-ink-muted">
                          Approved {decision.modifiedDates.length} of {item.dates.length} requested days
                        </p>
                      )}
                      {decision.comment && (
                        <p className="mt-2 text-small text-ink-muted">
                          {decision.supervisor}: &ldquo;{decision.comment}&rdquo;
                        </p>
                      )}
                      {((item.kind === 'leave' && (decision.state === 'approved' || decision.state === 'denied')) || (item.kind === 'schedule-submission' && decision.state === 'approved')) && !!item.endDate && item.endDate >= todayIso() && (
                        <div className="mt-3">
                          <button
                            type="button"
                            ref={(element) => {
                              if (element) commentTriggerRefs.current.set(`${item.id}-reversed`, element)
                              else commentTriggerRefs.current.delete(`${item.id}-reversed`)
                            }}
                            aria-expanded={openComment?.requestId === item.id && openComment.kind === 'reversed'}
                            onClick={() => openCommentFor(item.id, 'reversed')}
                            className="flex min-h-11 items-center justify-center rounded-control border border-line-strong px-4 text-small font-bold hover:bg-surface-2"
                          >
                            {decision.state === 'approved' ? 'Reverse approval' : 'Reverse denial'}
                          </button>
                          {openComment?.requestId === item.id && openComment.kind === 'reversed' && (
                            <div className="mt-3 flex flex-col gap-2">
                              <p className="text-small text-ink-muted">
                                The request returns to Awaiting a decision and {item.employee} is told.
                              </p>
                              <label htmlFor={`inbox-reverse-${item.id}`} className="text-caption font-bold text-ink-muted">
                                Reason (required)
                              </label>
                              <textarea
                                id={`inbox-reverse-${item.id}`}
                                value={commentDraft}
                                onChange={(event) => setCommentDraft(event.target.value.slice(0, COMMENT_MAX_LENGTH))}
                                rows={2}
                                className="w-full rounded-control border border-line-strong p-2.5 text-small"
                              />
                              <div className="flex gap-2">
                                <button
                                  type="button"
                                  disabled={commentDraft.trim() === ''}
                                  onClick={() => reverse(item, commentDraft.trim())}
                                  className="flex min-h-11 items-center justify-center rounded-control bg-primary px-4 text-small font-bold text-primary-ink hover:bg-primary-hover disabled:opacity-40"
                                >
                                  Confirm reversal
                                </button>
                                <button
                                  type="button"
                                  onClick={() => {
                                    commentTriggerRefs.current.get(`${item.id}-reversed`)?.focus()
                                    setOpenComment(null)
                                    setCommentDraft('')
                                  }}
                                  className="flex min-h-11 items-center justify-center rounded-control border border-line-strong px-4 text-small font-bold hover:bg-surface-2"
                                >
                                  Cancel
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </li>
                  )
                })}
              </ul>
            )}
          </section>
        </div>
      </main>
    </div>
  )
}
